import "./style.scss";

import { Pending } from "components/Management/ModalContent";
import ModalBox from "components/Modal";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { toDateOnly } from "helpers/beneficiaryDocuments";
import { formatCustomRoleId, getCleanStartorEndDay } from "helpers/utils";
import type { PossibleUserRole } from "hooks/usePossibleUserRoles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { APIKey } from "store/settingsStore";

import CreatedKeyStep, { type CreatedAPIKey } from "./CreatedKeyStep";

type TProps = {
    close: (success: boolean) => void;
    handleCreateKey: (options: Pick<APIKey, "name" | "keyScope" | "expiryDate">) => Promise<unknown>;
    roles: PossibleUserRole[];
    rolesReady: boolean;
    onKeepOpenChange?: (locked: boolean) => void;
};

const isCreatedApiKey = (value: unknown): value is CreatedAPIKey => {
    if (typeof value !== "object" || value === null || !("key" in value)) return false;
    return typeof value.key === "string" && value.key.length > 0;
};

const CreateKeyModal = ({ roles, rolesReady, close, handleCreateKey, onKeepOpenChange }: TProps) => {
    const [t] = useTranslation();
    const [isProcessing, setIsProcessing] = useState(false);

    const [name, setName] = useState("");
    const [scope, setScope] = useState("");
    const [expiryDate, setExpiryDate] = useState<Date | null>(null);
    const earliestExpiry = getCleanStartorEndDay(new Date());
    const [createdKey, setCreatedKey] = useState<CreatedAPIKey | null>(null);
    const [keyAcknowledged, setKeyAcknowledged] = useState(false);

    useEffect(() => {
        onKeepOpenChange?.(createdKey !== null);
    }, [createdKey, onKeepOpenChange]);

    useEffect(() => {
        if (roles.some((role) => role.role === scope)) return;
        const viewOnly = roles.find((role) => role.role === ROLES.VIEW_ONLY);
        setScope(viewOnly?.role ?? roles[0]?.role ?? "");
    }, [roles, scope]);

    const selectedRole = roles.find((role) => role.role === scope);

    const handleClick = async () => {
        if (!selectedRole) return;
        const keyScope =
            selectedRole.isCustom && selectedRole.id != null ? formatCustomRoleId(selectedRole.id) : selectedRole.role;
        setIsProcessing(true);
        try {
            const created = await handleCreateKey({
                name,
                keyScope,
                expiryDate: toDateOnly(expiryDate),
            });
            if (isCreatedApiKey(created)) {
                setCreatedKey(created);
            } else {
                toast.error(`${t("Settings.APIKey.Modals.CreateKey.MissingSecret")}`);
                close(true);
            }
        } catch {
            // The store already shows the error toast.
        } finally {
            setIsProcessing(false);
        }
    };

    if (createdKey) {
        return (
            <CreatedKeyStep
                createdKey={createdKey}
                keyAcknowledged={keyAcknowledged}
                onAcknowledge={setKeyAcknowledged}
                onClose={() => close(true)}
            />
        );
    }

    return (
        <>
            <ModalBox open={isProcessing} onClose={() => {}} showCloseButton={false}>
                <Pending phrase={t("Settings.APIKey.Modals.CreateKey.Creating")} />
            </ModalBox>
            <div className="create-key-wrapper">
                <div className="create-key-description">
                    <p>{t("Settings.APIKey.Modals.CreateKey.Impact")}</p>
                    <MessageBlock type="warning" text={t("Settings.APIKey.Modals.CreateKey.OnceGenerated")} small />
                </div>
                <div className="create-key-input">
                    <label className="create-key-input-label" htmlFor="">
                        {t("Settings.APIKey.Name")}
                    </label>
                    <StyledTextField
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("Settings.APIKey.Modals.CreateKey.NamePlaceholder")}
                    />
                </div>
                <div className="create-key-input">
                    <label className="create-key-input-label" htmlFor="">
                        {t("Settings.APIKey.Scope")}
                    </label>
                    <span className="create-key-input-hint">{t("Settings.APIKey.ScopeHint")}</span>
                    <StyledSelect
                        className="select"
                        displayEmpty
                        fullWidth
                        disabled={!rolesReady}
                        sx={{ fontFamily: "Poppins" }}
                        value={scope}
                        onChange={(e) => setScope(String(e.target.value))}
                        renderValue={() => <span>{selectedRole?.label ?? ""}</span>}
                        MenuProps={{
                            PaperProps: {
                                // MUI sets minWidth to the field width; width 0 keeps the menu from growing past it.
                                style: { width: 0 },
                            },
                        }}
                    >
                        {roles.map((role) => (
                            <StyledMenuItem
                                key={role.isCustom ? `custom-${role.id}` : role.role}
                                value={role.role}
                                sx={{
                                    whiteSpace: "normal",
                                    alignItems: "flex-start",
                                    height: "auto",
                                    width: "100%",
                                    py: 1,
                                }}
                            >
                                <span className="create-key-role-option">
                                    <span className="create-key-role-option-label">{role.label}</span>
                                    {role.description ? (
                                        <span className="create-key-role-option-description">{role.description}</span>
                                    ) : null}
                                </span>
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                    {selectedRole?.description ? (
                        <span className="create-key-input-hint">{selectedRole.description}</span>
                    ) : null}
                </div>
                <div className="create-key-input">
                    <label className="create-key-input-label" htmlFor="">
                        {t("Settings.APIKey.ExpiryDate")}{" "}
                        <span className="optional-mark">{t("Management.Optional")}</span>
                    </label>
                    <StyledDatePicker
                        value={expiryDate}
                        inputFormat="dd/MM/yyyy"
                        minDate={earliestExpiry}
                        maxYearDistance={10}
                        onChange={(value) => {
                            if (value instanceof Date && !Number.isNaN(value.getTime())) {
                                setExpiryDate(value);
                                return;
                            }
                            setExpiryDate(null);
                        }}
                        renderInput={(params) => <StyledTextField fullWidth {...params} />}
                    />
                </div>
                <div className="create-key-buttons">
                    <StyledButton onClick={() => close(false)} fullWidth buttonType="secondary">
                        {t("Settings.APIKey.Modals.CreateKey.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        fullWidth
                        disabled={!rolesReady || !selectedRole}
                        onClick={handleClick}
                    >
                        {t("Settings.APIKey.Modals.CreateKey.Generate")}
                    </StyledButton>
                </div>
            </div>
        </>
    );
};
export default CreateKeyModal;
